import type { QRPortalSubmitOrderItem, QRPortalOrderStatus } from "./qr-portal";
import type { KOTPrintPayload } from "./orders";

export interface QRPendingOrder {
  // Backend may return the id under any of these field names
  id?: string;
  pending_order_id?: string;
  qr_pending_order_id?: string;
  table_id: string | null;
  table_number: string | null;
  customer_name: string | null;
  outlet_id: string;
  items: QRPortalSubmitOrderItem[];
  status: QRPortalOrderStatus;
  rejection_reason: string | null;
  submitted_at: string;
  resolved_at: string | null;
  /** The table's open order, when it has one. */
  order_id?: string | null;
  // ---- Accept-to-claim (2026-10-29); absent on older servers ----
  /** submitted_at + 20 s: still pending after it, the order is auto-accepted. */
  accept_deadline?: string | null;
  /** "staff" (accept, approve or reject) or "auto" (the auto-accept). */
  resolved_via?: QRResolvedVia | null;
  resolved_by_name?: string | null;
  /** Why the auto-accept could not add the order; it stays pending for staff. */
  auto_accept_error?: string | null;
  /** At most 6 lines for the Accept prompt; `more_count` lines beyond them. */
  item_summary?: QRItemSummaryLine[];
  more_count?: number;
  /** Server clock when the response was built (ISO): countdowns correct for skew with it. */
  server_time?: string | null;
}

export type QRResolvedVia = "staff" | "auto";

export interface QRItemSummaryLine {
  name: string;
  quantity: number;
  variant?: string | null;
}

/** Get the canonical id for a pending order regardless of which field the backend used. */
export function getPendingOrderId(order: QRPendingOrder): string {
  return order.pending_order_id ?? order.qr_pending_order_id ?? order.id ?? "";
}

export interface QRPendingOrdersResponse {
  items: QRPendingOrder[];
  /** The server clock minus this device's when the list arrived (ms), if the list said. */
  skewMs?: number | null;
  /** Device clock (ms) when the list was asked for: it knows every order resolved before then. */
  requestedAt?: number;
}

/** POST .../qr-pending-orders/{id}/accept: Modify & accept sends `items` (qr_orders.manage). */
export interface QRAcceptPendingOrderRequest {
  items?: QRPortalSubmitOrderItem[];
  /** No KOT printer here: the outlet's tills print the accepted KOTs. */
  print_remotely?: boolean;
}

/**
 * The accept: the resolved order, the table's order and its new waiter (the
 * acceptor), and the KOT print payloads, shaped like POST /orders/{id}/confirm.
 */
export interface QRAcceptPendingOrderResponse {
  pending_order: QRPendingOrder;
  order_id: string;
  table_id: string | null;
  waiter: { id: string; name: string | null };
  kot_prints?: KOTPrintPayload[] | null;
  kot_escpos_data?: string | null;
  kot_copies?: number | null;
  /** The KOTs in kot_escpos_data (reported to POST /kot/printed once printed). */
  kot_ids?: string[] | null;
  /** print_remotely: the outlet's tills print the KOTs. */
  remote_print_requested?: boolean;
  print_request_ids?: string[] | null;
}

/** 409 QR_ORDER_ALREADY_RESOLVED `details`. */
export interface QRAlreadyResolvedDetails {
  status: "approved" | "rejected";
  resolved_via: QRResolvedVia | null;
  resolved_by_id: string | null;
  resolved_by_name: string | null;
}

/** `qr_order_pending` realtime metadata. */
export interface QROrderPendingEventMeta {
  pending_order_id: string;
  table_id?: string | null;
  table_number?: string | null;
  customer_name?: string | null;
  item_count?: number;
  submitted_at?: string | null;
  accept_deadline?: string | null;
  order_id?: string | null;
  item_summary?: QRItemSummaryLine[];
  more_count?: number;
}

/** `qr_order_resolved` realtime metadata (silent, not stored). */
export interface QROrderResolvedEventMeta {
  pending_order_id: string;
  table_id?: string | null;
  table_number?: string | null;
  status: "approved" | "rejected";
  resolved_via: QRResolvedVia;
  resolved_by_id?: string | null;
  resolved_by_name?: string | null;
  order_id?: string | null;
}

/** `qr_order_auto_accepted` / `qr_order_needs_attention` metadata. */
export interface QROrderFollowUpEventMeta {
  pending_order_id: string;
  table_id?: string | null;
  table_number?: string | null;
  order_id?: string | null;
  item_count?: number;
  waiter_assigned?: boolean;
  reason?: string;
}

/** `table_assist` metadata: a guest called a waiter or asked for the bill from the QR menu. */
export interface TableAssistEventMeta {
  assist_type: "call" | "bill";
  table_id?: string | null;
  table_number?: string | null;
  area_name?: string | null;
  table_session_id?: string | null;
  order_id?: string | null;
  /** Told only the table's waiter, or the floor team (captains, cashiers, managers). */
  routed_to?: "waiter" | "team";
}

export interface QRApprovePendingOrderRequest {
  items?: QRPortalSubmitOrderItem[];
}

export interface QRRejectPendingOrderRequest {
  reason?: string;
}

export interface QRTablePinResponse {
  pin: string;
  /** The outlet's Require table PIN setting (absent on older servers: true). */
  pin_required?: boolean;
}

export interface RegenerateTableQRTokenResponse {
  qr_token: string;
}

export interface BulkRegenerateTableQRTokensResponse {
  regenerated_count: number;
}
